(() => {
  if (customElements.get('shop-the-look-mto')) return;

  class ShopTheLookMTO extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this._abort = new AbortController();
      this._signal = this._abort.signal;
      this._hoverNone = window.matchMedia('(hover: none)');

      this.hotspots = Array.from(this.querySelectorAll('.stl-hotspot'));
      this.items = Array.from(this.querySelectorAll('.stl-item'));
      this.addAllBtn = this.querySelector('[data-add-all]');

      this._selectedId = this.hotspots[0]?.dataset.hotspotId || this.items[0]?.dataset.hotspotId || null;
      this._hoverId = null;

      this._bind();
      this._setActive(this._selectedId, { dim: false });
    }

    disconnectedCallback() {
      this._abort?.abort();
    }

    _bind() {
      const s = this._signal;

      this.hotspots.forEach((btn) => {
        const id = btn.dataset.hotspotId;

        // Pointer Enter: Desktop Only
        btn.addEventListener(
          'pointerenter',
          () => {
            if (this._hoverNone.matches) return; // Disable on mobile
            this._hoverId = id;
            this._setActive(id, { dim: true });
          },
          { signal: s }
        );

        // Pointer Leave: Desktop Only
        btn.addEventListener(
          'pointerleave',
          () => {
            if (this._hoverNone.matches) return; // Disable on mobile
            this._hoverId = null;
            this._setActive(this._selectedId, { dim: false });
          },
          { signal: s }
        );

        // Click: Updates internal state. Navigation happens via <a href>
        btn.addEventListener(
          'click',
          (e) => {
            this._selectedId = id;
            this._hoverId = null;
            this._setActive(id, { dim: false });
          },
          { signal: s }
        );
      });

      this.items.forEach((li) => {
        const id = li.dataset.hotspotId;

        li.addEventListener(
          'pointerenter',
          () => {
            if (this._hoverNone.matches) return;
            this._hoverId = id;
            this._setActive(id, { dim: true });
          },
          { signal: s }
        );

        li.addEventListener(
          'pointerleave',
          () => {
            if (this._hoverNone.matches) return;
            this._hoverId = null;
            this._setActive(this._selectedId, { dim: false });
          },
          { signal: s }
        );

        li.addEventListener(
          'click',
          () => {
            this._selectedId = id;
            this._hoverId = null;
            this._setActive(id, { dim: false });
          },
          { signal: s }
        );
      });

      this.addAllBtn?.addEventListener('click', () => this._addAllToCart(), { signal: s });
    }

    _setActive(id, { dim = false } = {}) {
      if (!id) return;

      this.hotspots.forEach((b) => {
        const active = b.dataset.hotspotId === id;
        b.classList.toggle('is-active', active);
        b.classList.toggle('is-dim', dim && !active);
      });

      this.items.forEach((li) => {
        const active = li.dataset.hotspotId === id;
        li.classList.toggle('is-active', active);
        li.classList.toggle('is-dim', dim && !active);
      });
    }

    _openCartDrawerNative() {
      const drawer =
        document.querySelector('cart-drawer-component') ||
        document.querySelector('cart-drawer') ||
        document.querySelector('[data-cart-drawer]');

      if (!drawer) return;

      if (typeof drawer.open === 'function') return drawer.open();
      if (typeof drawer.openDrawer === 'function') return drawer.openDrawer();
      if (typeof drawer.showDialog === 'function') return drawer.showDialog();

      drawer.setAttribute('open', '');
      drawer.dispatchEvent(new CustomEvent('open', { bubbles: true }));
    }

    _getCartCount(sections) {
      if (!sections) return null;
      for (const html of Object.values(sections)) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const el = doc.querySelector('[ref="cartItemCount"]');
        if (el) {
          const n = parseInt(el.textContent.trim(), 10);
          return isNaN(n) ? null : n;
        }
      }
      return null;
    }

    _dispatchCartUpdate(resource, sections) {
      const detail = {
        resource: resource || null,
        sourceId: 'shop-the-look-mto',
        data: {
          didError: false,
          source: 'shop-the-look-mto',
          itemCount: this._getCartCount(sections),
          sections: sections || undefined,
        },
      };

      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail }));
    }

    _flyToCart() {
      const cartIcon = document.querySelector('.header-actions__cart-icon');
      if (!cartIcon || !customElements.get('fly-to-cart')) return;
      const img = this.querySelector('.stl-item.is-active .stl-item-media img') ||
                  this.querySelector('.stl-item-media img');
      const imgUrl = img ? img.getAttribute('src') : null;
      if (!imgUrl) return;
      const el = document.createElement('fly-to-cart');
      el.classList.add('fly-to-cart--main');
      el.style.setProperty('background-image', `url(${imgUrl})`);
      el.style.setProperty('--start-opacity', '0');
      el.source = this.addAllBtn;
      el.destination = cartIcon;
      document.body.appendChild(el);
    }

    _animateAdded() {
      if (!this.addAllBtn) return;
      this.addAllBtn.setAttribute('data-added', 'true');
      setTimeout(() => this.addAllBtn.removeAttribute('data-added'), 900);
    }

    async _addAllToCart() {
      if (!this.addAllBtn) return;

      const variantIds = this.items
        .map((li) => li.dataset.variantId)
        .filter(Boolean)
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n) && n > 0);

      const unique = Array.from(new Set(variantIds));
      if (!unique.length) return;

      this._flyToCart();
      this.addAllBtn.disabled = true;
      this.addAllBtn.classList.add('is-loading');

      try {
        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionIds = Array.from(cartItemsComponents)
          .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
          .filter(Boolean);

        const cartAddUrl = (window.Theme?.routes?.cart_add_url) || '/cart/add.js';

        const payload = {
          items: unique.map((id) => ({ id, quantity: 1 })),
        };

        const url = new URL(cartAddUrl, window.location.origin);
        if (sectionIds.length) url.searchParams.set('sections', sectionIds.join(','));
        url.searchParams.set('sections_url', window.location.pathname);

        const addRes = await fetch(url.toString(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const response = await addRes.json();

        if (!addRes.ok || response?.status) {
          console.error('Shop the look add-all error:', response);
          return;
        }

        this._animateAdded();
        this._dispatchCartUpdate(response, response?.sections);
        this._openCartDrawerNative();
      } catch (e) {
        console.error(e);
      } finally {
        this.addAllBtn.disabled = false;
        this.addAllBtn.classList.remove('is-loading');
      }
    }
  }

  customElements.define('shop-the-look-mto', ShopTheLookMTO);
})();